import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Experience settings — voice, sound and motion preferences. All default to the
 * least-intrusive option (voice and sound off; motion follows the OS), so the
 * app is fully usable with every assistive flourish disabled. Persisted to
 * localStorage so an officer's choices survive a reload.
 */
export interface Settings {
  /** Web Speech recognition + synthesis for Ask Duty and spoken insights. */
  voice: boolean;
  /** WebAudio cues on new critical/high insights and while listening. */
  sound: boolean;
  /**
   * Motion preference. `system` follows `prefers-reduced-motion`; `reduce` and
   * `full` are explicit manual overrides.
   */
  motion: 'system' | 'reduce' | 'full';
}

const DEFAULTS: Settings = { voice: false, sound: false, motion: 'system' };

interface SettingsContextValue {
  settings: Settings;
  setVoice: (on: boolean) => void;
  setSound: (on: boolean) => void;
  setMotion: (m: Settings['motion']) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);
const STORAGE_KEY = 'duty.settings.v1';

function initialSettings(): Settings {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (!saved) return DEFAULTS;
    const parsed = JSON.parse(saved) as Partial<Settings>;
    return {
      voice: typeof parsed.voice === 'boolean' ? parsed.voice : DEFAULTS.voice,
      sound: typeof parsed.sound === 'boolean' ? parsed.sound : DEFAULTS.sound,
      motion:
        parsed.motion === 'reduce' || parsed.motion === 'full' || parsed.motion === 'system'
          ? parsed.motion
          : DEFAULTS.motion,
    };
  } catch {
    return DEFAULTS;
  }
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(initialSettings);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* storage unavailable — settings stay in-memory for this session */
    }
  }, [settings]);

  const value = useMemo<SettingsContextValue>(
    () => ({
      settings,
      setVoice: (on) => setSettings((s) => ({ ...s, voice: on })),
      setSound: (on) => setSettings((s) => ({ ...s, sound: on })),
      setMotion: (m) => setSettings((s) => ({ ...s, motion: m })),
    }),
    [settings],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
